import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {Formik} from 'formik';
import * as Yup from 'yup';

import Button from '../../components/ui-controls/Button';
import Dialog from '../../components/ui-controls/Dialog';
import DropDownPicker from '../../components/ui-controls/DropDownPicker';
import FormView from '../../components/ui-controls/form/FormView';
import Label from '../../components/ui-controls/form/Label';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';

const REQUIRED_FIELD_MESSAGE = 'This is a required field';

const ticketSchema = Yup.object().shape({
  firstName: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  lastName: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required(REQUIRED_FIELD_MESSAGE)
    .nullable(),
  nameOfStore: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  enquiryType: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  description: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
});

type SuccessDialogProps = {
  isVisible: boolean;
  onOk: () => void;
  submittedTicketNumber: string;
};
const SuccessDialog = ({
  isVisible,
  onOk,
  submittedTicketNumber,
}: SuccessDialogProps) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    onOk={onOk}
    okText="CLOSE"
    title="Your ticket was successfully submitted. Thank you.">
    <View>
      <Text style={styles.dialogText}>
        Your ticket number is #{submittedTicketNumber}
      </Text>
    </View>
  </Dialog>
);

type ErrorDialogProps = {
  isVisible: boolean;
  onOk: () => void;
};
const ErrorDialog = ({isVisible, onOk}: ErrorDialogProps) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Sorry, we couldn't submit your ticket at this moment. Please try again later."
  />
);

const HelpContactSubmitTicketScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.CONTACT_US_SUBMIT_TICKET>) => {
  const {shopSelectionStore, userStore} = useStores();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setErrorDialogVisible] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  // https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/rules#avoid-hard-coded-values 🤷🏻‍♂️
  const [enquiryTypes] = useState([
    {label: 'I am shopping in store', value: 'InStore'},
    {label: 'I am shopping online', value: 'Online'},
    {label: 'I am a store owner', value: 'LMG'},
  ]);

  const defaultValues = {
    firstName: userStore.firstName,
    lastName: userStore.lastName,
    email: userStore.emailAddress,
    nameOfStore: shopSelectionStore.selectedStore?.name,
    enquiryType: '',
    description: '',
  };

  const handleSuccessDialogOk = () => {
    setSuccessDialogVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  const handleErrorDialogOk = () => {
    setErrorDialogVisible(false);
  };

  const onSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      const response = await apiService.createSupportTicket(values);
      const {ticketNumber} = response;
      if (!ticketNumber) {
        console.error(
          'Support Ticket was submitted successfully but no ticket number was received',
          response,
        );
        throw new Error();
      }
      setSubmittedTicketNumber(ticketNumber);
      setSuccessDialogVisible(true);
    } catch (err) {
      setErrorDialogVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      keyboardShouldPersistTaps="always">
      <Formik
        initialValues={{...defaultValues}}
        onSubmit={onSubmit}
        validationSchema={() => ticketSchema}>
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched,
          initialValues,
          setFieldValue,
        }) => {
          const validationMessage = field => {
            return (
              <>
                {(!errors[field] || !touched[field]) && (
                  <ValidationMessage> </ValidationMessage>
                )}
                {!!(errors[field] && touched[field]) && (
                  <ValidationMessage>{errors[field]}</ValidationMessage>
                )}
              </>
            );
          };

          return (
            <ContentView testID="submit-ticket-screen">
              <FormView>
                <View>
                  <View>
                    <Label>First name</Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="name-given"
                      defaultValue={initialValues.firstName}
                      error={errors.firstName}
                      onBlur={handleBlur('firstName')}
                      onChangeText={handleChange('firstName')}
                      placeholder="First name"
                      touched={touched.firstName}
                      renderErrorMessage={false}
                    />
                    {validationMessage('firstName')}
                  </View>
                  <View>
                    <Label>Last name</Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="name-family"
                      defaultValue={initialValues.lastName}
                      error={errors.lastName}
                      onBlur={handleBlur('lastName')}
                      onChangeText={handleChange('lastName')}
                      placeholder="Last name"
                      touched={touched.lastName}
                      renderErrorMessage={false}
                    />
                    {validationMessage('lastName')}
                  </View>
                  <View>
                    <Label>Email</Label>
                    <TextInput
                      autoCapitalize="none"
                      autoComplete="email"
                      defaultValue={initialValues.email}
                      error={errors.email}
                      keyboardType="email-address"
                      onBlur={handleBlur('email')}
                      onChangeText={handleChange('email')}
                      placeholder="Email"
                      touched={touched.email}
                      renderErrorMessage={false}
                    />
                    {validationMessage('email')}
                  </View>
                  <View>
                    <Label>Enquiry Type</Label>
                    <DropDownPicker
                      items={enquiryTypes}
                      placeholder="Please select"
                      onSelectItem={enquiryType =>
                        setFieldValue('enquiryType', enquiryType.value)
                      }
                    />
                    {validationMessage('enquiryType')}
                  </View>
                  <View>
                    <Label>Name of store</Label>
                    <TextInput
                      autoCapitalize="words"
                      defaultValue={initialValues.nameOfStore}
                      error={errors.nameOfStore}
                      onBlur={handleBlur('nameOfStore')}
                      onChangeText={handleChange('nameOfStore')}
                      placeholder="Name of store"
                      style={styles.textInput}
                      touched={touched.nameOfStore}
                      renderErrorMessage={false}
                    />
                    {validationMessage('nameOfStore')}
                  </View>
                  <View>
                    <Label>How can we help you?</Label>
                    <TextInput
                      numberOfLines={4}
                      autoComplete="off"
                      defaultValue={initialValues.description}
                      error={errors.description}
                      multiline={true}
                      onBlur={handleBlur('description')}
                      onChangeText={handleChange('description')}
                      touched={touched.description}
                      renderErrorMessage={false}
                    />
                    {validationMessage('description')}
                  </View>
                </View>
                <Button
                  onPress={handleSubmit}
                  title="Submit ticket"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
                <SuccessDialog
                  isVisible={isSuccessDialogVisible}
                  onOk={handleSuccessDialogOk}
                  submittedTicketNumber={submittedTicketNumber}
                />
                <ErrorDialog
                  isVisible={isErrorDialogVisible}
                  onOk={handleErrorDialogOk}
                />
              </FormView>
            </ContentView>
          );
        }}
      </Formik>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  dialogText: {
    marginTop: 8,
    marginBottom: 8,
    fontSize: 16,
  },
});

export default HelpContactSubmitTicketScreen;
